// Enlaces de un sitio público, en un solo lugar. El mismo sitio vive en dos
// direcciones:
//   - en la app: `/s/<slug>` y `/s/<slug>/<página>`;
//   - en su subdominio (`<slug>.<dominio de sitios>`): `/` y `/<página>`.
// Lo elegido en los selectores va en `?picks=` en los dos casos.

import { normalizeBaseDomain } from "./host";

export interface SitePathInput {
  slug: string;
  /** Sin página: la primera del sitio. */
  page?: string;
  /** `picks` ya serializado (components/sites/picks.ts). */
  picks?: string;
  /** `true` en el subdominio del sitio; `false` en la app. */
  onSiteHost: boolean;
}

/** Ruta (sin origen) de una página del sitio. */
export function sitePagePath({ slug, page, picks, onSiteHost }: SitePathInput): string {
  const prefix = onSiteHost ? "" : `/s/${encodeURIComponent(slug)}`;
  const path = page ? `${prefix}/${encodeURIComponent(page)}` : prefix || "/";
  return picks ? `${path}?picks=${encodeURIComponent(picks)}` : path;
}

/** Dirección del sitio dentro de la app: `/s/<slug>`. */
export const siteAppPath = (slug: string): string => sitePagePath({ slug, onSiteHost: false });

/**
 * URL del sitio en su subdominio (`https://<slug>.<base>`), o `null` si no
 * hay dominio de sitios configurado.
 */
export function siteSubdomainUrl(slug: string, baseDomain: string | undefined | null): string | null {
  const base = normalizeBaseDomain(baseDomain);
  return base ? `https://${slug}.${base}` : null;
}

/**
 * `true` si `pathname` es un sitio público dentro de la app (`/s/<slug>`,
 * `/s/<slug>/<página>`). Ahí no va nada del ERP: ni la pantalla de licencia,
 * ni el service worker ni el manifiesto de la PWA (DevTools y el asistente
 * viven en el layout `_authenticated`, que estas rutas no usan).
 */
export function isPublicSitePath(pathname: string): boolean {
  return /^\/s\/[^/]+(\/|$)/.test(pathname);
}
